import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./Users.css";
import { useUser } from "../contexts/UserContext";
import { useLot } from "../contexts/LotContext";
import Modal from "../components/Modal";
import api from "../utils/api";
import EditUserModal, { type EditUser } from "../components/Superadmin/EditUserModal";
import AddUserModal from "../components/Superadmin/AddUserModal";
import { formatDisplayUserId } from "../utils/format";
import Menu from "../components/Menu";
import {
  isSuperadminModeEnabled,
  SUPERADMIN_MODE_EVENT,
  SUPERADMIN_MODE_STORAGE_KEY,
} from "../components/SuperadminHeader";

type LotRole = "owner" | "operator" | "staff";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizeAccountRole = (role?: string | null) => (role || "").replace(/\s+/g, "").toLowerCase();

const toLotUser = (data: any): EditUser => ({
  userId: data?.userId || "",
  name: data?.name || "Unknown User",
  email: data?.email || "unknown",
  phoneNo: data?.phoneNo || "",
  role: data?.role || "",
  displayUserId: data?.displayUserId || data?.displayuserId,
});

const lotRoleLabel = (role: LotRole) => {
  if (role === "owner") return "Owner";
  if (role === "operator") return "Operator";
  return "Staff";
};

const generateTempPassword = () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  let password = "";

  for (let i = 0; i < 3; i += 1) {
    password += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  for (let i = 0; i < 3; i += 1) {
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
  }
  return password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
};

interface LotUserRow {
  lotRole: LotRole;
  user: EditUser;
}

const superadminIcon = "/assets/superadmin/superadmin.svg";
const addUserIcon = "/assets/superadmin/add-user.svg";
const editIcon = "/assets/Edit2.svg";
const removeIcon = "/assets/Minus.svg";
const changeOwnerIcon = "/assets/superadmin/change_owner.svg";
const dropdownIcon = "/assets/ArrowDropdown.svg";

const Users: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const { user } = useUser();
  const { lotData, invalidateData, isDemoLot } = useLot();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [owner, setOwner] = useState<EditUser | null>(null);
  const [operators, setOperators] = useState<EditUser[]>([]);
  const [staff, setStaff] = useState<EditUser[]>([]);

  const [removeLoadingId, setRemoveLoadingId] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{
    role: Exclude<LotRole, "owner">;
    user: EditUser;
  } | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addUserRole, setAddUserRole] = useState<Exclude<LotRole, "owner">>("operator");
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [isAddUserLoading, setIsAddUserLoading] = useState(false);
  const [pendingInviteEmail, setPendingInviteEmail] = useState<string | null>(null);
  const [addUserEmail, setAddUserEmail] = useState("");
  const [candidateUsers, setCandidateUsers] = useState<EditUser[]>([]);
  const [isCandidateLoading, setIsCandidateLoading] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [candidateSearch, setCandidateSearch] = useState("");
  const addUserPrimaryLabel = addUserRole === "operator" ? "Operator" : "Staff";
  const addUserConfirmLabel = pendingInviteEmail ? "Send Invite" : `Add ${addUserPrimaryLabel}`;

  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [ownerModalError, setOwnerModalError] = useState<string | null>(null);
  const [isOwnerModalLoading, setIsOwnerModalLoading] = useState(false);
  const [ownerCandidateId, setOwnerCandidateId] = useState<string | null>(null);
  const [ownerCandidateSearch, setOwnerCandidateSearch] = useState("");

  const [editUser, setEditUser] = useState<EditUser | null>(null);
  const [removeAccessModalOpen, setRemoveAccessModalOpen] = useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [createUserMode, setCreateUserMode] = useState<"assign" | "owner">("assign");

  const accountRole = (user?.role || "").toLowerCase();
  const isAccountSuperadmin = accountRole === "superadmin";
  const [isSuperadminModeOn, setIsSuperadminModeOn] = useState<boolean>(() => isSuperadminModeEnabled());
  const canUseSuperadminTools = isAccountSuperadmin && isSuperadminModeOn;

  const ownerIdFromLot = lotData?.ownerId;
  const lotOwnerId = owner?.userId || ownerIdFromLot;
  const currentUserId = user?.userId || "";

  const isOwner = Boolean(lotOwnerId && lotOwnerId === currentUserId);
  const isOperator = operators.some((op) => op.userId === currentUserId);
  const isStaffMember = staff.some((member) => member.userId === currentUserId);

  const canModifyLotUsers = isOwner || canUseSuperadminTools;
  const canChangeOwner = canUseSuperadminTools;
  const canAddUsers = canModifyLotUsers;
  const demoBlocksAction = isDemoLot && !canUseSuperadminTools;
  const [demoBlockMessage, setDemoBlockMessage] = useState<string | null>(null);
  const shouldLoadCandidates = canUseSuperadminTools && (isAddModalOpen || isOwnerModalOpen);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setIsSuperadminModeOn(detail.enabled);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setIsSuperadminModeOn(event.newValue === "true");
      }
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const fetchOwner = useCallback(async () => {
    if (!ownerIdFromLot) {
      setOwner(null);
      return;
    }
    try {
      const response = await api.get(`/users/get-user-by-id/${ownerIdFromLot}`);
      const data = response.data?.data;
      if (data) {
        setOwner(toLotUser(data));
      }
    } catch (err) {
      console.error("Failed to fetch owner", err);
      setOwner(null);
    }
  }, [ownerIdFromLot]);

  const fetchOperators = useCallback(async () => {
    if (!lotId) return;
    try {
      const response = await api.get(`/parkinglots/get-all-operators/${lotId}`);
      const list = Array.isArray(response.data?.data) ? response.data.data.map(toLotUser) : [];
      setOperators(list);
    } catch (err) {
      console.error("Failed to fetch operators", err);
      setOperators([]);
    }
  }, [lotId]);

  const fetchStaff = useCallback(async () => {
    if (!lotId) return;
    try {
      const response = await api.get(`/parkinglots/get-all-staff/${lotId}`);
      const list = Array.isArray(response.data?.data) ? response.data.data.map(toLotUser) : [];
      setStaff(list);
    } catch (err) {
      console.error("Failed to fetch staff", err);
      setStaff([]);
    }
  }, [lotId]);

  const loadUsers = useCallback(async () => {
    if (!lotId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      await Promise.all([fetchOwner(), fetchOperators(), fetchStaff()]);
      setError(null);
    } catch (err) {
      console.error("Failed to load users", err);
      setError("Failed to load lot users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [lotId, fetchOwner, fetchOperators, fetchStaff]);

useEffect(() => {
  loadUsers();
}, [loadUsers]);

useEffect(() => {
  setOwner(null);
  setOperators([]);
  setStaff([]);
  setIsLoading(true);
}, [lotId, ownerIdFromLot]);

useEffect(() => {
  if (!shouldLoadCandidates) {
    setCandidateUsers([]);
    setCandidateError(null);
    setSelectedCandidateId(null);
    setCandidateSearch("");
    setOwnerCandidateId(null);
    setOwnerCandidateSearch("");
    return;
  }

  let cancelled = false;
  const fetchCandidates = async () => {
    setIsCandidateLoading(true);
    setCandidateError(null);
    try {
      const response = await api.get("/users/get-all-users");
      const payload = response.data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      if (!cancelled) {
        setCandidateUsers(list.map((item: any) => toLotUser(item)));
      }
    } catch (err) {
      console.error("Failed to load users", err);
      if (!cancelled) {
        setCandidateError("Failed to load existing users.");
        setCandidateUsers([]);
      }
    } finally {
      if (!cancelled) {
        setIsCandidateLoading(false);
      }
    }
  };

  fetchCandidates();
  return () => {
    cancelled = true;
  };
}, [shouldLoadCandidates]);

  const rows: LotUserRow[] = useMemo(() => {
    const list: LotUserRow[] = [];
    if (owner) {
      list.push({ lotRole: "owner", user: owner });
    }
    const showFullRoster = !isStaffMember || canUseSuperadminTools || isOwner;
    if (showFullRoster) {
      operators.forEach((op) => {
        if (op.userId !== owner?.userId) {
          list.push({ lotRole: "operator", user: op });
        }
      });
      staff.forEach((member) => {
        if (member.userId !== owner?.userId) {
          list.push({ lotRole: "staff", user: member });
        }
      });
    } else {
      const self = staff.find((member) => member.userId === currentUserId);
      if (self && self.userId !== owner?.userId) {
        list.push({ lotRole: "staff", user: self });
      }
    }
    return list;
  }, [owner, operators, staff, isStaffMember, canUseSuperadminTools, isOwner, currentUserId]);

  const currentRow = rows.find((row) => row.user.userId === currentUserId) || null;

  const allowedCandidates = useMemo(() => {
    if (!canUseSuperadminTools) return [];
    return candidateUsers.filter((candidate) => {
      const role = (candidate.role || "").toLowerCase();
      return role === "operator" || role === "superadmin";
    });
  }, [candidateUsers, canUseSuperadminTools]);

  const searchCandidates = useCallback(
    (term: string) => {
      const normalized = term.trim().toLowerCase();
      if (!normalized) {
        return allowedCandidates;
      }
      return allowedCandidates.filter((candidate) => {
        const id = candidate.displayUserId?.replace("PWP-U-", "") || candidate.displayUserId || "";
        return (
          candidate.email?.toLowerCase().includes(normalized) ||
          candidate.name?.toLowerCase().includes(normalized) ||
          id.toLowerCase().includes(normalized)
        );
      });
    },
    [allowedCandidates]
  );

  const filteredCandidates = useMemo(
    () => searchCandidates(candidateSearch).slice(0, 20),
    [candidateSearch, searchCandidates]
  );

  const ownerFilteredCandidates = useMemo(
    () => searchCandidates(ownerCandidateSearch).slice(0, 20),
    [ownerCandidateSearch, searchCandidates]
  );

  const candidateMenuItems = useMemo(
    () =>
      filteredCandidates.map((candidate) => {
        const formattedId = candidate.displayUserId?.replace("PWP-U-", "") || candidate.displayUserId || "";
        const isSuperadminRole = (candidate.role || "").toLowerCase() === "superadmin";
        return {
          key: candidate.userId,
          label: (
            <div className="users-candidate-option">
              <div className="users-candidate-name">
                {candidate.name || "Unnamed User"}
                {isSuperadminRole && <img src={superadminIcon} alt="Superadmin" className="users-superadmin-icon" />}
              </div>
              <div className="users-candidate-email">{candidate.email || "unknown"}</div>
              <div className="users-candidate-id">{formattedId}</div>
            </div>
          ),
          selected: candidate.userId === selectedCandidateId,
          onSelect: () => {
            setSelectedCandidateId(candidate.userId);
            setAddUserEmail(candidate.email || "");
            setPendingInviteEmail(null);
            setAddUserError(null);
          },
        };
      }),
    [filteredCandidates, selectedCandidateId]
  );

  const ownerCandidateMenuItems = useMemo(
    () =>
      ownerFilteredCandidates.map((candidate) => {
        const formattedId = candidate.displayUserId?.replace("PWP-U-", "") || candidate.displayUserId || "";
        const isSuperadminRole = (candidate.role || "").toLowerCase() === "superadmin";
        return {
          key: candidate.userId,
          label: (
            <div className="users-candidate-option">
              <div className="users-candidate-name">
                {candidate.name || "Unnamed User"}
                {isSuperadminRole && <img src={superadminIcon} alt="Superadmin" className="users-superadmin-icon" />}
              </div>
              <div className="users-candidate-email">{candidate.email || "unknown"}</div>
              <div className="users-candidate-id">{formattedId}</div>
            </div>
          ),
          selected: candidate.userId === ownerCandidateId,
          onSelect: () => {
            setOwnerCandidateId(candidate.userId);
            setOwnerModalError(null);
          },
        };
      }),
    [ownerFilteredCandidates, ownerCandidateId]
  );

  const selectedCandidate = useMemo(
    () => allowedCandidates.find((user) => user.userId === selectedCandidateId) || null,
    [allowedCandidates, selectedCandidateId]
  );

  const ownerSelectedCandidate = useMemo(
    () => allowedCandidates.find((user) => user.userId === ownerCandidateId) || null,
    [allowedCandidates, ownerCandidateId]
  );

  const existingUserTriggerLabel = selectedCandidate
    ? selectedCandidate.email || selectedCandidate.name || "Selected user"
    : "Select user…";

  const ownerCandidateTriggerLabel = ownerSelectedCandidate
    ? ownerSelectedCandidate.email || ownerSelectedCandidate.name || "Selected owner"
    : "Select new owner…";

  const currentRoleLabel = useMemo(() => {
    if (currentRow) {
      return lotRoleLabel(currentRow.lotRole);
    }
    if (isOwner) return "Owner";
    if (isOperator) return "Operator";
    if (isStaffMember) return "Staff";
    return canUseSuperadminTools ? "Super Admin" : "Viewer";
  }, [currentRow, isOwner, isOperator, isStaffMember, canUseSuperadminTools]);

  const closeAddUserModal = () => {
    setIsAddModalOpen(false);
    setAddUserEmail("");
    setAddUserRole("operator");
    setAddUserError(null);
    setPendingInviteEmail(null);
    setSelectedCandidateId(null);
    setCandidateSearch("");
  };

  const closeOwnerModal = () => {
    setIsOwnerModalOpen(false);
    setOwnerModalError(null);
    setOwnerCandidateId(null);
    setOwnerCandidateSearch("");
    setIsOwnerModalLoading(false);
  };

  const attachUserToLot = async (targetRole: LotRole, targetUserId: string) => {
    if (!lotId) return;
    const payload = {
      lotId,
      userId: targetUserId,
      modifiedBy: currentUserId || lotOwnerId || "system",
    };

    if (targetRole === "operator") {
      await api.post("/parkinglots/add-operator", payload);
      await fetchOperators();
    } else if (targetRole === "staff") {
      await api.post("/parkinglots/add-staff", payload);
      await fetchStaff();
    } else if (targetRole === "owner") {
      await api.post("/parkinglots/change-owner", payload);
      const previousOwnerId = owner?.userId;
      if (previousOwnerId && previousOwnerId !== targetUserId) {
        const fallbackPayload = {
          lotId,
          userId: previousOwnerId,
          modifiedBy: currentUserId || previousOwnerId,
        };
        try {
          await api.post("/parkinglots/add-operator", fallbackPayload);
        } catch (err) {
          console.warn("Failed to demote previous owner to operator", err);
        }
        const wasStaff = staff.some((member) => member.userId === previousOwnerId);
        if (wasStaff) {
          try {
            await api.delete("/parkinglots/remove-staff", { data: fallbackPayload });
          } catch (err) {
            console.warn("Failed to remove previous owner from staff list", err);
          }
        }
      }
      await Promise.all([fetchOwner(), fetchOperators(), fetchStaff()]);
      invalidateData();
    }
  };

  const handleLotRoleChange = async (
    userId: string,
    currentRole: LotRole,
    nextRole: LotRole
  ) => {
    if (currentRole === nextRole || !lotId) return;
    try {
      if (nextRole === "owner") {
        await attachUserToLot("owner", userId);
        return;
      }

      const payload = {
        lotId,
        userId,
        modifiedBy: currentUserId || lotOwnerId || "system",
      };

      if (nextRole === "operator") {
        await api.post("/parkinglots/add-operator", payload);
        if (currentRole === "staff") {
          await api.delete("/parkinglots/remove-staff", { data: payload });
        }
      } else if (nextRole === "staff") {
        await api.post("/parkinglots/add-staff", payload);
        if (currentRole === "operator") {
          await api.delete("/parkinglots/remove-operator", { data: payload });
        }
      }
      await loadUsers();
    } catch (err) {
      console.error("Failed to update lot role", err);
      alert("Unable to update lot role. Please try again.");
    }
  };

  const handleRemoveUser = async (targetRole: Exclude<LotRole, "owner">, targetUserId: string) => {
    if (!canModifyLotUsers || !lotId) return;
    setRemoveLoadingId(`${targetRole}-${targetUserId}`);
    const payload = {
      lotId,
      userId: targetUserId,
      modifiedBy: currentUserId || lotOwnerId || "system",
    };
    try {
      const endpoint = targetRole === "operator" ? "/parkinglots/remove-operator" : "/parkinglots/remove-staff";
      await api.delete(endpoint, { data: payload });
      if (targetRole === "operator") {
        await fetchOperators();
      } else {
        await fetchStaff();
      }
    } catch (err) {
      console.error("Failed to remove user", err);
      alert("Failed to remove user. Please try again.");
    } finally {
      setRemoveLoadingId(null);
      setPendingRemoval((current) =>
        current && current.user.userId === targetUserId && current.role === targetRole ? null : current
      );
    }
  };

  const handleRemoveOwnAccess = async () => {
    if (!lotId || !currentUserId) return;
    try {
      const payload = {
        lotId,
        userId: currentUserId,
        modifiedBy: currentUserId,
      };
      const endpoint = isOperator ? "/parkinglots/remove-operator" : "/parkinglots/remove-staff";
      await api.delete(endpoint, { data: payload });
      setRemoveAccessModalOpen(false);
      window.location.href = "/lot-dashboard";
    } catch (err) {
      console.error("Failed to remove access", err);
      alert("Unable to remove access. Please try again.");
    }
  };

  const upgradeVehicleOwnerAccount = async (existingUser: any, fallbackEmail: string) => {
    const normalizedRole = normalizeAccountRole(existingUser?.role);
    if (normalizedRole !== "vehicleowner") {
      return existingUser;
    }
    const userId = existingUser?.userId;
    if (!userId) {
      throw new Error("Unable to identify user to upgrade.");
    }
    const safeEmail = (existingUser?.email || fallbackEmail).toLowerCase();
    const response = await api.post("/users/upgrade-vehicle-owner", {
      email: safeEmail,
      lotRole: addUserRole === "operator" ? "Operator" : "Staff",
    });
    const updatedUser = response?.data?.data || {};
    return { ...existingUser, ...updatedUser, role: updatedUser.role || "Operator" };
  };

  const handleAddUserSubmit = async () => {
    if (selectedCandidateId) {
      try {
        setIsAddUserLoading(true);
        await attachUserToLot(addUserRole, selectedCandidateId);
        closeAddUserModal();
      } catch (err: any) {
        setAddUserError(err?.response?.data?.message || "Failed to add user.");
      } finally {
        setIsAddUserLoading(false);
      }
      return;
    }

    const email = addUserEmail.trim().toLowerCase();
    if (!emailRegex.test(email)) {
      setAddUserError("Enter a valid email address.");
      return;
    }

    try {
      setIsAddUserLoading(true);
      setAddUserError(null);
      const response = await api.get("/users/get-user-by-email", { params: { email } });
      let userData = response.data?.data;
      if (!userData?.userId) {
        setAddUserError("Unable to locate that user.");
        return;
      }
      if (normalizeAccountRole(userData.role) === "vehicleowner") {
        try {
          userData = await upgradeVehicleOwnerAccount(userData, email);
        } catch (upgradeError: any) {
          const message =
            upgradeError?.response?.data?.message ||
            upgradeError?.message ||
            "Unable to upgrade account to operator.";
          setAddUserError(message);
          return;
        }
      }
      await attachUserToLot(addUserRole, userData.userId);
      closeAddUserModal();
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setPendingInviteEmail(email);
        setSelectedCandidateId(null);
        setAddUserError("User does not exist. Send invite?");
      } else {
        setAddUserError(err?.response?.data?.message || "Failed to add user.");
      }
    } finally {
      setIsAddUserLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!pendingInviteEmail) return;
    try {
      setIsAddUserLoading(true);
      setAddUserError(null);
      const tempPassword = generateTempPassword();
      const fallbackName = pendingInviteEmail.split("@")[0] || "New User";
      const response = await api.post("/users/create-user", {
        name: fallbackName,
        email: pendingInviteEmail,
        tempPassword,
        phoneNo: "",
        role: "Operator",
        createdBy: currentUserId || "system",
      });
      const createdId =
        response.data?.data?.user?.userId ||
        response.data?.data?.userId;
      if (!createdId) {
        setAddUserError("Failed to create user.");
        return;
      }
      await attachUserToLot(addUserRole, createdId);
      closeAddUserModal();
    } catch (err: any) {
      setAddUserError(err?.response?.data?.message || "Failed to send invite.");
    } finally {
      setIsAddUserLoading(false);
    }
  };

  const handleCreateUserModalConfirm = async (newUserId?: string) => {
    setIsCreateUserModalOpen(false);
    if (!newUserId) {
      setCreateUserMode("assign");
      return;
    }
    if (createUserMode === "owner") {
      try {
        setIsOwnerModalLoading(true);
        await attachUserToLot("owner", newUserId);
        closeOwnerModal();
      } catch (err: any) {
        setOwnerModalError(err?.response?.data?.message || "Unable to update owner.");
      } finally {
        setIsOwnerModalLoading(false);
        setCreateUserMode("assign");
      }
      return;
    }

    try {
      setIsAddUserLoading(true);
      await attachUserToLot(addUserRole, newUserId);
      closeAddUserModal();
    } catch (err: any) {
      setAddUserError(err?.response?.data?.message || "Failed to add new user to lot.");
    } finally {
      setIsAddUserLoading(false);
      setCreateUserMode("assign");
    }
  };

  const handleOwnerModalConfirm = async () => {
    if (!ownerCandidateId) {
      setOwnerModalError("Select a user to assign as owner.");
      return;
    }
    try {
      setIsOwnerModalLoading(true);
      setOwnerModalError(null);
      await attachUserToLot("owner", ownerCandidateId);
      closeOwnerModal();
    } catch (err: any) {
      setOwnerModalError(err?.response?.data?.message || "Unable to update owner.");
    } finally {
      setIsOwnerModalLoading(false);
    }
  };

  const renderRoleControl = (row: LotUserRow) => {
    const canChange =
      row.lotRole !== "owner" &&
      (isOwner || canUseSuperadminTools);

    if (!canChange) {
      return <span className="users-role-pill">{lotRoleLabel(row.lotRole)}</span>;
    }

    const options: LotRole[] = canUseSuperadminTools ? ["owner", "operator", "staff"] : ["operator", "staff"];

    return (
      <Menu
        trigger={
          <button type="button" className="users-role-select">
            {lotRoleLabel(row.lotRole)}
            <img src={dropdownIcon} alt="" aria-hidden="true" />
          </button>
        }
        items={options.map((opt) => ({
          key: opt,
          label: lotRoleLabel(opt),
          selected: opt === row.lotRole,
          onSelect: () => {
            if (demoBlocksAction) {
              setDemoBlockMessage("Cannot change user roles on a demo lot.");
              return;
            }
            handleLotRoleChange(row.user.userId, row.lotRole, opt);
          },
        }))}
        dropdownClassName="users-role-menu"
        matchTriggerWidth={false}
      />
    );
  };

  const renderActions = (row: LotUserRow) => {
    const actions: React.ReactNode[] = [];

    if (row.lotRole === "owner") {
      if (canChangeOwner) {
        actions.push(
          <button
            key="change-owner"
            className="users-icon-button"
            onClick={(e) => {
              e.stopPropagation();
              setOwnerCandidateId(null);
              setOwnerCandidateSearch("");
              setOwnerModalError(null);
              setIsOwnerModalOpen(true);
            }}
            title="Change owner"
          >
            <img src={changeOwnerIcon} alt="Change owner" />
          </button>
        );
      }
    } else if (canModifyLotUsers) {
        actions.push(
          <button
            key="remove"
            className="users-icon-button"
            onClick={(e) => {
              e.stopPropagation();
              if (demoBlocksAction) {
                setDemoBlockMessage("Cannot remove users from a demo lot.");
                return;
              }
              setPendingRemoval({ role: row.lotRole as "operator" | "staff", user: row.user });
            }}
            disabled={removeLoadingId === `${row.lotRole}-${row.user.userId}`}
            title="Remove from lot"
          >
            <img src={removeIcon} alt="Remove user" />
          </button>
        );
    }

    if (canUseSuperadminTools) {
      actions.push(
        <button
          key="edit"
          className="users-icon-button"
          onClick={(e) => {
            e.stopPropagation();
            setEditUser(row.user);
          }}
          title="Edit user"
        >
          <img src={editIcon} alt="Edit user" />
        </button>
      );
    }

    return <div className="users-row-actions">{actions}</div>;
  };

  const isViewerNonOwner = !isOwner && !canUseSuperadminTools;

  if (isLoading) {
    return (
      <div className="users-page">
        <h1>Users</h1>
        <p className="users-subtitle">Manage who can administer and support this lot.</p>
        <div className="table-container users-table-skeleton">
          <div className="skeleton-block users-table-skeleton-block" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <h1>Users</h1>
      <p className="users-subtitle">Manage who can administer and support this lot.</p>
      {error && <div className="users-error">{error}</div>}
      <div className="table-container">
        <table className="app-table users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Lot Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.lotRole}-${row.user.userId}`}
                className={row.user.userId === currentUserId ? "users-row-current" : undefined}
              >
                <td>
                  <div className="users-row-name">
                    <div className="users-row-primary">
                      {row.user.name}
                      {(row.user.role || "").toLowerCase() === "superadmin" && (
                        <img src={superadminIcon} alt="Superadmin" className="users-superadmin-icon" />
                      )}
                    </div>
                    <div className="users-row-secondary">
                      #{formatDisplayUserId(row.user.displayUserId)} · {row.user.email}
                    </div>
                  </div>
                </td>
                <td className="users-row-role">{renderRoleControl(row)}</td>
                <td className="users-actions-cell">{renderActions(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canAddUsers && (
        <div className="users-add-footer">
          <button
            className="button-primary"
            onClick={() => {
              if (demoBlocksAction) {
                setDemoBlockMessage("Cannot add users to a demo lot.");
                return;
              }
              setIsAddModalOpen(true);
            }}
          >
            <img src={addUserIcon} alt="" aria-hidden="true" />
            <span>Add User</span>
          </button>
        </div>
      )}

      {demoBlockMessage && (
        <Modal
          isOpen={true}
          title={demoBlockMessage}
          description="This action is disabled while the lot is in demo mode."
          confirmText=""
          cancelText="OK"
          onConfirm={() => { }}
          onCancel={() => setDemoBlockMessage(null)}
        />
      )}
      {isViewerNonOwner && (
        <div className="remove-access-section">
          <button
            className="remove-access-button"
            onClick={() => setRemoveAccessModalOpen(true)}
          >
            <img src="/assets/trash.svg" alt="Remove Access" className="remove-access-icon" />
            Remove Access
          </button>
        </div>
      )}

      {/* Add User Modal */}
      {isAddModalOpen && (
        <Modal
          isOpen={true}
          title="Add User"
          description={canUseSuperadminTools ? "Select an existing account or create a new one." : "Enter the user's email and choose their lot role."}
          confirmText={addUserConfirmLabel}
          cancelText="Cancel"
          onConfirm={pendingInviteEmail ? handleSendInvite : handleAddUserSubmit}
          onCancel={closeAddUserModal}
          disableConfirm={
            isAddUserLoading ||
            (!pendingInviteEmail &&
              !(canUseSuperadminTools ? Boolean(selectedCandidateId) : emailRegex.test(addUserEmail.trim())))
          }
        >
          {canUseSuperadminTools ? (
            <>
              <div className="users-modal-field users-modal-field-full">
                <label>Select User</label>
                <Menu
                  trigger={
                    <button type="button" className="users-modal-select users-modal-select-full">
                      {existingUserTriggerLabel}
                      <img src={dropdownIcon} alt="" aria-hidden="true" />
                    </button>
                  }
                  items={candidateMenuItems}
                  customContent={
                    <input
                      type="text"
                      className="users-candidate-search"
                      placeholder="Search by email, ID, or name"
                      value={candidateSearch}
                      onChange={(e) => setCandidateSearch(e.target.value)}
                    />
                  }
                  dropdownClassName="users-role-menu users-candidate-menu"
                  maxHeight="30vh"
                  matchTriggerWidth
                  onOpenChange={(open) => {
                    if (!open) {
                      setCandidateSearch("");
                    }
                  }}
                />
                <div className="users-candidate-meta">
                  <button
                    type="button"
                    className="button-secondary button-compact"
                    onClick={() => {
                      setCreateUserMode("assign");
                      setIsCreateUserModalOpen(true);
                    }}
                  >
                    New User
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="users-modal-field">
              <label>Email</label>
              <input
                type="email"
                value={addUserEmail}
                onChange={(e) => {
                  setAddUserEmail(e.target.value);
                  setAddUserError(null);
                  setPendingInviteEmail(null);
                  setSelectedCandidateId(null);
                }}
                placeholder="email@example.com"
              />
            </div>
          )}
          {canUseSuperadminTools && candidateError && (
            <p className="users-modal-error">{candidateError}</p>
          )}
          <div className="users-modal-field users-modal-field-full">
            <label>Lot Role</label>
            <Menu
              trigger={
                <button type="button" className="users-modal-select users-modal-select-full">
                  {addUserRole === "operator" ? "Operator" : "Staff"}
                  <img src={dropdownIcon} alt="" aria-hidden="true" />
                </button>
              }
            items={[
              {
                key: "operator",
                label: "Operator",
                selected: addUserRole === "operator",
                onSelect: () => setAddUserRole("operator"),
              },
              {
                key: "staff",
                label: "Staff",
                selected: addUserRole === "staff",
                onSelect: () => setAddUserRole("staff"),
              },
            ]}
            matchTriggerWidth={false}
            dropdownClassName="users-role-menu"
          />
          </div>
          {pendingInviteEmail && !canUseSuperadminTools && (
            <p className="users-modal-hint">
              User does not exist. Would you like to send them a create-account email?
            </p>
          )}
          {addUserError && <p className="users-modal-error">{addUserError}</p>}
        </Modal>
      )}
      {pendingRemoval && (
        <Modal
          isOpen={true}
          title={`Remove ${pendingRemoval.user.name || pendingRemoval.user.email || "User"}`}
          description={`This user will immediately lose their ${lotRoleLabel(pendingRemoval.role).toLowerCase()} access to the lot.`}
          confirmText={
            removeLoadingId === `${pendingRemoval.role}-${pendingRemoval.user.userId}`
              ? "Removing..."
              : `Remove ${lotRoleLabel(pendingRemoval.role)}`
          }
          cancelText="Cancel"
          onConfirm={() => handleRemoveUser(pendingRemoval.role, pendingRemoval.user.userId)}
          onCancel={() => {
            if (!removeLoadingId) {
              setPendingRemoval(null);
            }
          }}
          disableConfirm={removeLoadingId === `${pendingRemoval.role}-${pendingRemoval.user.userId}`}
        />
      )}
      {canUseSuperadminTools && (
        <AddUserModal
          isOpen={isCreateUserModalOpen}
          onClose={() => {
            setIsCreateUserModalOpen(false);
            setCreateUserMode("assign");
          }}
          onCreated={handleCreateUserModalConfirm}
          title="Create User"
        />
      )}

      {/* Owner Change Modal */}
      {canChangeOwner && isOwnerModalOpen && (
        <Modal
          isOpen={true}
          title="Change Lot Owner"
          description="Select the account to promote to owner. The current owner will become an operator."
          confirmText={isOwnerModalLoading ? "Updating..." : "Change Owner"}
          cancelText="Cancel"
          onConfirm={handleOwnerModalConfirm}
          onCancel={closeOwnerModal}
          disableConfirm={isOwnerModalLoading || !ownerCandidateId}
        >
          <div className="users-modal-field users-modal-field-full">
            <label>Select New Owner</label>
            <Menu
              trigger={
                <button type="button" className="users-modal-select users-modal-select-full">
                  {ownerCandidateTriggerLabel}
                  <img src={dropdownIcon} alt="" aria-hidden="true" />
                </button>
              }
              items={ownerCandidateMenuItems}
              customContent={
                <input
                  type="text"
                  className="users-candidate-search"
                  placeholder="Search by email, ID, or name"
                  value={ownerCandidateSearch}
                  onChange={(e) => setOwnerCandidateSearch(e.target.value)}
                />
              }
              dropdownClassName="users-role-menu users-candidate-menu"
              maxHeight="30vh"
              matchTriggerWidth
              onOpenChange={(open) => {
                if (!open) {
                  setOwnerCandidateSearch("");
                }
              }}
            />
            <div className="users-candidate-meta">
              <button
                type="button"
                className="button-secondary button-compact"
                onClick={() => {
                  setCreateUserMode("owner");
                  setIsCreateUserModalOpen(true);
                }}
              >
                New User
              </button>
            </div>
          </div>
          {candidateError && <p className="users-modal-error">{candidateError}</p>}
          {ownerModalError && <p className="users-modal-error">{ownerModalError}</p>}
        </Modal>
      )}

      {/* Edit User Modal */}
      {canUseSuperadminTools && (
        <EditUserModal
          isOpen={Boolean(editUser)}
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={async () => {
            setEditUser(null);
            await loadUsers();
          }}
        />
      )}
      {isViewerNonOwner && (
        <Modal
          isOpen={removeAccessModalOpen}
          title="Remove Access"
          description="If you remove access to this parking lot you will not be able to use any of the tools. You may require assistance from Parallel to regain access."
          confirmText="Remove Access"
          cancelText="Cancel"
          onConfirm={handleRemoveOwnAccess}
          onCancel={() => setRemoveAccessModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Users;
